import React from "react";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import {
  $createParagraphNode,
  $getRoot,
  LexicalEditor as LexicalEditorType,
  ParagraphNode,
  TextNode,
} from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";

import LexicalTheme from "./LexicalTheme";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { LexicalEditorTypes } from "./LexicalEditor.types";
import { InlineImageNode } from "./plugins/ImageNode";
// import { MaxLengthPlugin } from "./plugins/MaxLengthPlugin";
import { $generateNodesFromDOM } from "@lexical/html";
import { ExtendedTextNode } from "./plugins/TextNode";
import { CustomListNode } from "./plugins/NumberListNode";
import "./LexicalStyles.scss";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { CustomLinkNode } from "./plugins/CustomLinkNode";

const LexicalEditor = ({
  placeholderText = "",
  options = [],
  changeStateEditorBody,
  messageBody,
  style,
  readOnly = 0,
}: // maxLength = 1000,
LexicalEditorTypes) => {
  const config = {
    namespace: "React.js Demo",
    editable: !Boolean(readOnly),
    nodes: [
      ListNode,
      ListItemNode,
      ParagraphNode,
      TextNode,
      HeadingNode,
      QuoteNode,
      InlineImageNode,
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text),
        withKlass: ExtendedTextNode,
      },
      CustomListNode,
      LinkNode,
      AutoLinkNode,
      CustomLinkNode,
    ],
    onError(error: Error) {
      console.log("error in editor", error);
      throw error;
    },
    theme: LexicalTheme,
    editorState: (editor: LexicalEditorType) => {
      const root = $getRoot();

      if (messageBody) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(messageBody, "text/html");
        const nodes = $generateNodesFromDOM(editor, dom);

        editor.update(() => {
          const root = $getRoot();
          root.append(...nodes);
        });
      } else if (root.isEmpty()) {
        const paragraph = $createParagraphNode();
        root.append(paragraph);
      }
    },
  };
  return (
    <LexicalComposer initialConfig={config}>
      <div className="lexical-editorContainer" style={{ ...style }}>
        <ToolbarPlugin
          options={options}
          changeStateEditorBody={changeStateEditorBody}
          readOnly={Boolean(readOnly)}
          // maxLength={maxLength}
          messageBody={messageBody}
        />
        <div className="lexical-editorInner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-editorInput"
                aria-placeholder={placeholderText}
                placeholder={
                  <div className="lexical-editorPlaceholder">
                    {placeholderText}
                  </div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          <OnChangePlugin onChange={(editorState) => console.log("editor")} />
          {/* <MaxLengthPlugin maxLength={maxLength} /> */}
        </div>
      </div>
    </LexicalComposer>
  );
};

export default LexicalEditor;
